import { handbuchDateiname } from '../../services/pdf/generatorHandbuch';
import { stabilerInhaltsHash } from '../../services/ablageHilfen';
import type { ReportMetadata } from '../../types';
import type { Handbuch, HandbuchLage, HistorieEintrag } from './types';
import type { KapitelQuelle } from './useHandbuch';

/**
 * Das SiGe-Handbuch in der zentralen Ablage (Stufe 1A, Entscheid E17).
 *
 * WANN abgelegt wird, entscheidet `HandbuchModul`; hier steht, WAS abgelegt
 * wird — Titel, Dateiname, Stand, Fassungskennung — rein und ohne DOM
 * getestet. Den Ablageweg selbst kennt `unterlagenAblage.ts`.
 *
 * ═══ ZWEI ANLAESSE ═════════════════════════════════════════════════════════
 *   'freigabe' — die Freigabe friert das Handbuch ein (Entscheidung Nr. 8)
 *                und schreibt einen Vermerk in `historie`. Genau dieser
 *                Vermerk IST die Fassung; seine Nummer und der Zeitpunkt der
 *                Freigabe bilden die Kennung. Abgelegt wird automatisch —
 *                und wenn das scheitert, bleibt die Freigabe gueltig, denn
 *                die Ablage ist eine Zweitschrift, kein Teil der Freigabe.
 *   'entwurf'  — nur per Knopf, deutlich als Entwurf beschriftet. Die
 *                Kennung ist der Inhaltsstand, damit derselbe Entwurf nicht
 *                zweimal liegt.
 */

export type HandbuchAblageAnlass = 'freigabe' | 'entwurf';

const text = (v?: string | null): string => (v == null ? '' : String(v)).trim();
const zweistellig = (n: number): string => String(n).padStart(2, '0');

/** „TT.MM.JJJJ" in Ortszeit. Ein reiner ISO-Tag bleibt ein Tag, keine Uhrzeitverschiebung. */
export const datumKurz = (iso: string | null | undefined): string => {
    const t = text(iso);
    const tag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (tag) return `${tag[3]}.${tag[2]}.${tag[1]}`;
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return t;
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** „HH:MM" in Ortszeit; leer, wenn der Wert keine Uhrzeit traegt. */
export const uhrzeitKurz = (iso: string | null | undefined): string => {
    const t = text(iso);
    if (!/T\d{2}:\d{2}/.test(t)) return '';
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return '';
    return `${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// FASSUNG
// ═══════════════════════════════════════════════════════════════════════════

/** Der Stand, der abgelegt wird — unabhaengig davon, woher die Bytes kommen. */
export interface HandbuchFassung {
    anlass: HandbuchAblageAnlass;
    handbuchId: string;
    /** Fassungsnummer aus der Historie („2.0") — nur bei der Freigabe. */
    nr: string | null;
    /** Vom Anwender vergebener Fassungsname, falls vorhanden. */
    versionLabel: string | null;
    /** ISO-Zeitpunkt: Freigabe bzw. Klick. */
    zeitpunkt: string;
    autor: string | null;
    /** Aenderungsvermerk der Freigabe. */
    aenderungen: string | null;
    /** Inhaltsstand — nur beim Entwurf; die Freigabe hat ihre Nummer. */
    inhaltsStand: string | null;
}

const nachId = <T extends { id?: string }>(liste: readonly T[] | null | undefined): T[] =>
    [...(liste || [])].sort((a, b) => String(a?.id || '').localeCompare(String(b?.id || '')));

/**
 * Die Kapitelquellen, soweit sie das Blatt veraendern (`useHandbuch.KapitelQuelle`):
 * Plan, Notrufplan, Bewertungen, Ablage, Planfassungen. Sie werden erst beim
 * Oeffnen des Kapitel-Reiters geladen — der Inhaltsstand eines Entwurfs wird
 * deshalb unmittelbar vor dem Ablegen gebildet, wenn sie da sind.
 */
export type KapitelQuellenStand = Partial<Pick<KapitelQuelle,
    'sigeProject' | 'arbeitsschritte' | 'gefaehrdungen' | 'massnahmen' | 'notrufplan'
    | 'paare' | 'dokumente' | 'planFassungen'>>;

/**
 * Der Inhaltsstand eines Handbuch-Entwurfs: Kopfsatz, Gewerkeseiten,
 * Nachweise — und die Kapitelquellen, wenn sie vorliegen, denn auch sie
 * stehen auf dem Blatt. Zeitstempel der Datenbank fallen in der
 * Serialisierung weg.
 */
export const handbuchInhaltsStand = (lage: HandbuchLage, quelle?: KapitelQuellenStand | null): string =>
    stabilerInhaltsHash({
        handbuch: lage?.handbuch ?? null,
        gewerke: nachId(lage?.gewerke),
        nachweise: nachId(lage?.nachweise),
        quelle: quelle ? {
            sigeProject: quelle.sigeProject ?? null,
            arbeitsschritte: nachId(quelle.arbeitsschritte),
            gefaehrdungen: nachId(quelle.gefaehrdungen),
            massnahmen: nachId(quelle.massnahmen),
            notrufplan: quelle.notrufplan ?? null,
            paare: nachId(quelle.paare as ReadonlyArray<{ id?: string }> | undefined),
            dokumente: nachId(quelle.dokumente),
            planFassungen: nachId(quelle.planFassungen),
        } : null,
    });

/**
 * Die Fassung einer Freigabe.
 *
 * `eintrag` ist der Vermerk, den `freigebenHandbuch` an die Historie
 * gehaengt hat; `handbuch` die zurueckgegebene Zeile MIT `freigegeben_am`.
 * Faellt `freigegeben_am` einmal aus (Altbestand), traegt das Tagesdatum
 * des Vermerks — eine Kennung ohne Zeitpunkt ist besser als keine.
 */
export const fassungAusFreigabe = (
    handbuch: Pick<Handbuch, 'id' | 'freigegeben_am' | 'version_label'>,
    eintrag: Pick<HistorieEintrag, 'nr' | 'datum' | 'autor' | 'aenderungen' | 'version_label'>,
): HandbuchFassung => ({
    anlass: 'freigabe',
    handbuchId: text(handbuch?.id),
    nr: text(eintrag?.nr) || null,
    versionLabel: text(eintrag?.version_label) || text(handbuch?.version_label) || null,
    zeitpunkt: text(handbuch?.freigegeben_am) || text(eintrag?.datum),
    autor: text(eintrag?.autor) || null,
    aenderungen: text(eintrag?.aenderungen) || null,
    inhaltsStand: null,
});

/**
 * Der Vermerk der GELTENDEN Freigabe — der letzte Eintrag der Historie.
 *
 * `freigebenHandbuch` haengt den Vermerk ans Ende, und eine neue Fassung
 * setzt den Status zurueck auf `entwurf`; bei einem eingefrorenen Handbuch
 * ist der letzte Eintrag deshalb immer die Freigabe. `null` bei einem
 * Entwurf oder einer leeren Historie.
 */
export const geltendeFreigabe = (handbuch: Handbuch | null | undefined): HistorieEintrag | null => {
    if (!handbuch || handbuch.status !== 'freigegeben') return null;
    const historie = handbuch.historie || [];
    return historie.length ? historie[historie.length - 1] : null;
};

/**
 * Die Fassung fuer den Knopf „Fassung in Dokumente ablegen" an einem
 * eingefrorenen Handbuch — dieselbe Kennung wie beim automatischen Ablegen,
 * damit die Ablage einen zweiten Versuch als denselben Stand erkennt.
 * `null`, wenn das Handbuch nicht freigegeben ist.
 */
export const fassungDerGeltendenFreigabe = (handbuch: Handbuch | null | undefined): HandbuchFassung | null => {
    if (!handbuch || handbuch.status !== 'freigegeben') return null;
    const vermerk = geltendeFreigabe(handbuch);
    return fassungAusFreigabe(handbuch, vermerk || {
        nr: '', datum: text(handbuch.freigegeben_am).slice(0, 10), autor: handbuch.freigegeben_von_name || '',
        aenderungen: '', version_label: handbuch.version_label || '',
    });
};

/** Fassung eines von Hand abgelegten Entwurfs. */
export const entwurfsFassung = (
    lage: HandbuchLage,
    jetztIso: string,
    quelle?: KapitelQuellenStand | null,
): HandbuchFassung | null => {
    if (!lage?.handbuch) return null;
    return {
        anlass: 'entwurf',
        handbuchId: text(lage.handbuch.id),
        nr: null,
        versionLabel: text(lage.handbuch.version_label) || null,
        zeitpunkt: text(jetztIso),
        autor: null,
        aenderungen: null,
        inhaltsStand: handbuchInhaltsStand(lage, quelle),
    };
};

/**
 * Der Inhaltsstand eines Entwurfs, neu gebildet aus dem Stand, der JETZT
 * gedruckt wird — unmittelbar vor dem Ablegen, wenn die Kapitelquellen da
 * sind. Eine Freigabe bleibt unveraendert: ihre Kennung ist Nummer und
 * Zeitpunkt, nicht der Inhalt.
 */
export const mitInhaltsStand = (
    f: HandbuchFassung,
    lage: HandbuchLage,
    quelle?: KapitelQuellenStand | null,
): HandbuchFassung =>
    (f.anlass === 'entwurf' ? { ...f, inhaltsStand: handbuchInhaltsStand(lage, quelle) } : f);

/**
 * Stabile Kennung des Standes fuer `fassung_ref`.
 *
 * Freigabe: Nummer UND Zeitpunkt. Die Nummer allein ist je Handbuch eindeutig
 * (`naechsteFassungsNummer` zaehlt aus dem Bestand hoch), der Zeitpunkt
 * schuetzt vor dem Fall, dass eine Historie von Hand bereinigt wurde.
 * Entwurf: der Inhaltsstand.
 */
export const handbuchFassungRef = (f: HandbuchFassung): string => {
    const id = f.handbuchId || 'ohne-id';
    if (f.anlass === 'freigabe') {
        return `handbuch:${id}:fassung:${f.nr || 'ohne-nr'}:${f.zeitpunkt || 'ohne-zeit'}`;
    }
    return `handbuch:${id}:entwurf:${f.inhaltsStand || 'ohne-stand'}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// TITEL, STAND, DATEINAME, BESCHREIBUNG
// ═══════════════════════════════════════════════════════════════════════════

/** „Fassung 2.0" bzw. „Fassung 2.0 „Bauabschnitt B"" — Nummer zuerst, Name dazu. */
const fassungsName = (f: HandbuchFassung): string => {
    const nr = text(f.nr);
    const label = text(f.versionLabel);
    if (nr && label && label !== nr) return `Fassung ${nr} „${label}"`;
    if (nr) return `Fassung ${nr}`;
    if (label) return `Fassung „${label}"`;
    return 'Fassung';
};

/**
 * Anzeigename in der Ablage.
 *
 * Die Fassung traegt Nummer und Tag — so steht es auch in der Historie. Der
 * Entwurf traegt zusaetzlich die Uhrzeit, weil es an einem Tag mehrere geben
 * kann und sie sich sonst in der Liste nicht unterscheiden liessen. Das Wort
 * „Entwurf" setzt die Ablage SELBST davor (Praefix „ENTWURF — " und Chip,
 * `unterlagenAblage.ts`); hier stuende es sonst zweimal.
 */
export const handbuchAblageTitel = (f: HandbuchFassung): string => {
    if (f.anlass === 'freigabe') {
        return `SiGe-Handbuch — ${fassungsName(f)} vom ${datumKurz(f.zeitpunkt)}`;
    }
    const uhr = uhrzeitKurz(f.zeitpunkt);
    return `SiGe-Handbuch — Stand vom ${datumKurz(f.zeitpunkt)}${uhr ? `, ${uhr}` : ''}`;
};

/** Kurzer Stand fuer die Spalte „Stand" der Ablage: Fassungsname, beim Entwurf der Tag. */
export const handbuchAblageStand = (f: HandbuchFassung): string =>
    (f.anlass === 'freigabe' ? fassungsName(f) : datumKurz(f.zeitpunkt));

/**
 * Dateiname — ueber `handbuchDateiname`, damit die abgelegte Datei so heisst
 * wie die gedruckte. Der Entwurf traegt das Wort statt einer Fassung.
 */
export const handbuchAblageDateiname = (f: HandbuchFassung, projektName: string): string =>
    handbuchDateiname(
        projektName || '',
        f.anlass === 'freigabe' ? (text(f.versionLabel) || text(f.nr) || null) : 'Entwurf',
        f.zeitpunkt,
    );

/** Beschreibung — sagt dem Leser in der Ablage, WIE die Zeile entstanden ist. */
export const handbuchAblageBeschreibung = (f: HandbuchFassung): string => {
    if (f.anlass === 'freigabe') {
        const wer = f.autor ? ` durch ${f.autor}` : '';
        const vermerk = f.aenderungen ? ` Änderungsvermerk: ${f.aenderungen}` : '';
        return `Bei der Freigabe${wer} am ${datumKurz(f.zeitpunkt)} automatisch abgelegt. `
            + `Das Handbuch ist mit dieser Fassung eingefroren.${vermerk}`;
    }
    // „Entwurfsstand — nicht freigegeben." haengt die Ablage selbst an.
    const uhr = uhrzeitKurz(f.zeitpunkt);
    return `Von Hand abgelegt am ${datumKurz(f.zeitpunkt)}${uhr ? ` um ${uhr}` : ''} — `
        + 'die Kapitel können sich noch ändern.';
};

// ═══════════════════════════════════════════════════════════════════════════
// AUFTRAG AN DIE ABLAGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Ausgabe fuer den Aenderungsdienst (Stufe 6.1). Der ANKER — der
 * Steckbrief-Auszug, gegen den spaetere Aenderungen der Planungsphase
 * verglichen werden — gehoert zur FREIGABE, nicht zur Ablage: `HandbuchModul`
 * haelt ihn im Moment der Freigabe fest (`halteFreigabeFest` des Kerns, Anlass
 * `freigabe`, dieselbe Fassungskennung). Die Ablage kann Tage spaeter
 * nachgeholt werden; sie schickt deshalb `anker: null` und bildet NIE einen
 * eigenen — ein heutiger Stand fuer eine alte Freigabe verdeckte echte
 * Abweichungen. Der Kern prueft nur, ob zur Fassung ein Anker liegt, und
 * meldet sonst, dass sie nicht ueberwacht wird. `metadata` bleibt der Form
 * halber (Eingabe des Kerns), wird ohne Anker aber nicht gelesen.
 */
export interface HandbuchAusgabe {
    unterlage: 'handbuch';
    metadata: Partial<ReportMetadata> | null;
    /** Immer `null`: der Anker wurde bei der Freigabe festgehalten (siehe oben). */
    anker: null;
}

/**
 * Nur die FREIGABE ist eine Ausgabe des Handbuchs. Ein von Hand abgelegter
 * Entwurf bekommt keinen Journal-Eintrag — er ist kein Stand, nach dem
 * gearbeitet wird, und ein Anker darauf meldete Abweichungen zu einem Blatt,
 * das nie galt.
 */
export const handbuchAusgabe = (
    anlass: HandbuchAblageAnlass,
    metadata?: Partial<ReportMetadata> | null,
): HandbuchAusgabe | undefined =>
    (anlass === 'freigabe' ? { unterlage: 'handbuch', metadata: metadata ?? null, anker: null } : undefined);

/**
 * Der Auftrag an `legeUnterlageAb` — strukturgleich mit dessen Eingabe,
 * aber hier ohne Abhaengigkeit zum Dienst, damit die Datei rein bleibt.
 */
export interface HandbuchAblageAuftrag {
    projectId: string;
    modul: 'handbuch';
    datei: Blob;
    dateiname: string;
    titel: string;
    stand: string;
    fassungRef: string;
    anlass: HandbuchAblageAnlass;
    beschreibung: string;
    /** Nur bei der Freigabe gesetzt (`handbuchAusgabe`). */
    ausgabe?: HandbuchAusgabe;
}

export const baueHandbuchAblageAuftrag = (
    projectId: string,
    datei: Blob,
    f: HandbuchFassung,
    projektName: string,
    metadata?: Partial<ReportMetadata> | null,
): HandbuchAblageAuftrag => ({
    projectId,
    modul: 'handbuch',
    datei,
    dateiname: handbuchAblageDateiname(f, projektName),
    titel: handbuchAblageTitel(f),
    stand: handbuchAblageStand(f),
    fassungRef: handbuchFassungRef(f),
    anlass: f.anlass,
    beschreibung: handbuchAblageBeschreibung(f),
    ausgabe: handbuchAusgabe(f.anlass, metadata),
});

/**
 * Die Hinweise des Kerns zu einer Ablage — etwa, dass der Journal-Eintrag
 * ohne Planungsphase entstand. Sie sind kein Fehler: die Fassung liegt. Leere
 * und doppelte Saetze fallen weg; die Oberflaeche zeigt sie dezent.
 */
export const ablageHinweise = (ergebnis: { hinweise?: readonly string[] | null } | null | undefined): string[] =>
    Array.from(new Set((ergebnis?.hinweise || []).map(h => text(h)).filter(Boolean)));

/**
 * Darf abgelegt werden?
 *
 * Nein ohne gespeichertes Projekt ('temp-id'); nein in der Nur-Ansicht — die
 * Ablage schreibt ans Projekt, und das Handbuch sperrt fein je Aktion. Der
 * Grund wird angezeigt, nie verschluckt.
 */
export const sollHandbuchAblegen = (
    a: { projectId: string | null | undefined; isReadOnly: boolean },
): { status: 'ok' } | { status: 'fehler'; grund: string } => {
    const id = text(a.projectId);
    if (!id || id === 'temp-id') {
        return { status: 'fehler', grund: 'Das Projekt ist noch nicht gespeichert — das Handbuch wurde nicht in Dokumente abgelegt.' };
    }
    if (a.isReadOnly) {
        return { status: 'fehler', grund: 'Nur-Ansicht: das Handbuch wurde nicht in Dokumente abgelegt.' };
    }
    return { status: 'ok' };
};

// ═══════════════════════════════════════════════════════════════════════════
// ERNEUT ABLEGEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Was „Erneut ablegen" tut.
 *
 * Kam der erste Versuch bis zum Auftrag (PDF erzeugt, Groesse geprueft) und
 * scheiterte erst am Netz, geht GENAU dieser Auftrag noch einmal — dieselben
 * Bytes, dieselbe Kennung, derselbe Titel. Das Blatt neu zu erzeugen hiesse,
 * den inzwischen vielleicht bearbeiteten oder erneut freigegebenen Inhalt
 * unter der alten Kennung abzulegen (Codex-Review 17.10.2026). Der
 * Steckbrief-Anker haengt nicht am Versuch: er wurde bei der Freigabe
 * festgehalten (`HandbuchAusgabe`).
 *
 * Kam es nicht bis zum Auftrag (PDF scheiterte, zu gross), gibt es nichts
 * Festgehaltenes: dann wird NEU abgelegt — mit der Fassung des HEUTIGEN
 * Standes, nie mit der alten.
 */
export type AblageWiederholung =
    | { art: 'auftrag'; auftrag: HandbuchAblageAuftrag }
    | { art: 'neu' };

export const ablageWiederholung = (
    letzter: { auftrag?: HandbuchAblageAuftrag | null } | null | undefined,
): AblageWiederholung =>
    (letzter?.auftrag ? { art: 'auftrag', auftrag: letzter.auftrag } : { art: 'neu' });
